import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './ChangePassword.css';

function ChangePassword({ required = false }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const validatePasswordStrength = (password) => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (name === 'newPassword') {
      setPasswordStrength(validatePasswordStrength(value));
    }

    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Validate password strength
    const strength = validatePasswordStrength(formData.newPassword);
    const allRequirementsMet = Object.values(strength).every(req => req === true);

    if (!allRequirementsMet) {
      setError('New password does not meet all requirements');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      alert('Password changed successfully!');
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const allRequirementsMet = Object.values(passwordStrength).every(req => req === true);

  return (
    <div className="change-password-container">
      <div className="change-password-box">
        <div className="change-password-header">
          <h1>Change Password</h1>
          {required && (
            <p className="required-notice">
              You must change your password before accessing the system.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="change-password-form">
          {error && (
            <div className="error-banner">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              required
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          {formData.newPassword && (
            <div className="password-requirements">
              <h4>Password Requirements:</h4>
              <ul>
                <li className={passwordStrength.length ? 'met' : ''}>
                  {passwordStrength.length ? '✓' : '○'} At least 8 characters
                </li>
                <li className={passwordStrength.uppercase ? 'met' : ''}>
                  {passwordStrength.uppercase ? '✓' : '○'} At least one uppercase letter (A-Z)
                </li>
                <li className={passwordStrength.lowercase ? 'met' : ''}>
                  {passwordStrength.lowercase ? '✓' : '○'} At least one lowercase letter (a-z)
                </li>
                <li className={passwordStrength.number ? 'met' : ''}>
                  {passwordStrength.number ? '✓' : '○'} At least one number (0-9)
                </li>
                <li className={passwordStrength.special ? 'met' : ''}>
                  {passwordStrength.special ? '✓' : '○'} At least one special character (!@#$%^&*...)
                </li>
              </ul>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading || !allRequirementsMet || formData.newPassword !== formData.confirmPassword}
          >
            {loading ? 'Changing Password...' : 'Change Password'}
          </button>

          {!required && (
            <button
              type="button"
              onClick={() => navigate('/')}
              className="btn btn-secondary btn-block"
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

export default ChangePassword;
